import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Search, Target, Sparkles, Users, FileText, Shield, ArrowRight, ChevronDown, ChevronRight } from "lucide-react";

const CLIENT_ARTICLES = [
  {
    id: "intent-data", icon: Target, category: "Core Concept",
    title: "What is Intent Data?",
    summary: "Understanding the proprietary advantage that powers Varyynt Intel.",
    content: [
      "Traditional sourcing tools like LinkedIn Recruiter, Biospace, and retained search firms tell you where a candidate has been — their title history, employer list, skills keywords. This is backward-looking data. Every recruiter in your space has it.",
      "Varyynt's consumer platform asks passive professionals a different question: What does your ideal next role look like? They describe the company type, therapeutic focus, role scope, geography, work environment, and career trajectory that would make them engage.",
      "Varyynt Intel gives your hiring team anonymized access to this intent data. When you run a search and score candidates, you're not just matching skills to a JD — you're matching opportunity to aspiration. The candidates at the top of your ranked list aren't just qualified. They're directionally interested in what you're offering.",
      "This is the difference between cold outreach with a 3% response rate and warm engagement with a 40%+ response rate. Intent data eliminates the most expensive part of executive recruiting: time spent pursuing people who were never going to say yes.",
    ],
  },
  {
    id: "role-search", icon: Search, category: "Getting Started",
    title: "Creating a Role Search",
    summary: "How to define criteria and explore the talent pool.",
    content: [
      "Navigate to New Role Search from the sidebar. Give your search a name (e.g., \u201cVP Medical Affairs — Oncology\u201d) and select whether it's an Active Recruit or Succession Planning search.",
      "Use the criteria panel on the left to select seniority levels, functional areas, therapeutic areas, geography, and employer type. Each selection instantly updates the live pool count — you're querying our real candidate database.",
      "Pro tip: Start broad, then narrow. Begin with just seniority + functional area to see the full addressable pool, then add therapeutic area and geography to focus.",
      "The intent signal fields at the bottom (company type preference, work environment, role scope notes) don't change the count. They feed into the AI scoring engine during Deep Analysis — the more context you provide here, the more accurate the intent matching.",
    ],
  },
  {
    id: "deep-analysis", icon: Sparkles, category: "Core Feature",
    title: "Running Deep Analysis",
    summary: "How AI scoring works and what the scores mean.",
    content: [
      "Deep Analysis runs two scoring engines against every candidate in your filtered pool:",
      "Experience Fit (0\u2013100): A structured score based on seniority match, functional area match, therapeutic area overlap, years of experience, and degree. This is the \u201ccan they do the job\u201d score.",
      "Intent Alignment (0\u2013100): An AI-powered score based on how closely the candidate's stated career preferences match what your role offers. This is the \u201cwould they want the job\u201d score.",
      "The Composite Score weights these 60/40 (experience/intent). A candidate scoring 85+ is both highly qualified and directionally aligned with your opportunity. This is the list you want to engage.",
      "Use the minimum score slider to filter results, and the \u201cSelect all \u2265 X\u201d button to quickly build your outreach list. The candidates you select go directly into a Work Order.",
    ],
  },
  {
    id: "work-orders", icon: FileText, category: "Taking Action",
    title: "Work Orders & Outreach",
    summary: "From intelligence to engagement.",
    content: [
      "A Work Order is your formal request to engage candidates through Varyynt. Select scored candidates, choose your outreach type, and submit.",
      "Job Promotion: Send a specific role description to selected candidates. Best for roles where the company brand is an asset.",
      "De-Identified Outreach: Gauge interest without revealing your company identity. Best for confidential searches, competitive hires, or succession planning where discretion matters.",
      "The Varyynt team handles all outreach. Candidates respond to Varyynt, not directly to you — which means they're honest about their interest level without the pressure of a direct recruiter pitch.",
      "Track progress in the Work Orders dashboard: outreach sent, responses received, interested candidates, and consented candidates ready for introduction.",
    ],
  },
  {
    id: "privacy", icon: Shield, category: "Privacy & Trust",
    title: "Anonymization & Privacy",
    summary: "How candidate data is protected throughout the platform.",
    content: [
      "You never see candidate names, contact information, LinkedIn profiles, or current employer names. Every candidate is identified only by an anonymous ID (e.g., A-0042) and their de-identified profile.",
      "Your organization's suppression profile ensures that your own employees never appear in your search results. This is enforced server-side before any data reaches your browser.",
      "Candidates are not aware that any specific company is viewing their anonymized profile. They only learn about interest when they receive a de-identified outreach through Varyynt — and they choose whether to respond.",
      "This two-sided anonymity creates a safe space for both hiring teams and candidates to explore fit without premature commitment.",
    ],
  },
];

const ADMIN_ARTICLES = [
  {
    id: "admin-demo-funnel", icon: Users, category: "Sales Operations",
    title: "Demo Invite & Conversion Funnel",
    summary: "From prospect to paid client.",
    content: [
      "The demo invite workflow is your primary top-of-funnel tool. From Client Management, click \u201cInvite to Demo\u201d to send a branded email to a prospect.",
      "Demo orgs have full search and Deep Analysis access, but their Work Orders are flagged as demo and don't trigger real fulfillment. This lets prospects experience the platform's power without creating work for your team.",
      "When a prospect is ready to sign, use the Upgrade button to convert Demo \u2192 Paid. This enables real Work Order fulfillment, removes the demo flag, and sends a notification email to their admin users.",
      "Track conversion metrics in the Activity Feed — look for demo_invite_sent and demo_to_paid_upgrade events to measure your funnel.",
    ],
  },
  {
    id: "admin-suppression", icon: Shield, category: "Data Operations",
    title: "Suppression Profile Management",
    summary: "Setting up and testing employer suppression.",
    content: [
      "Every client org needs a suppression profile before going live. This prevents their own employees from appearing in search results.",
      "Add all variations of the company name (e.g., \u201cNovo Nordisk\u201d, \u201cNovo Nordisk Inc.\u201d, \u201cNNI\u201d, \u201cNovo Nordisk A/S\u201d). Also add all email domains (e.g., \u201cnovonordisk.com\u201d, \u201cnn.com\u201d).",
      "Use the Test Suppression feature to verify your configuration. Enter any company name or domain and see immediately whether it would be suppressed.",
      "Conservative matching: if any signal matches, the candidate is suppressed. False positives (suppressing too many) are preferred over false negatives (showing an employee).",
    ],
  },
  {
    id: "admin-audit", icon: Target, category: "Compliance",
    title: "Audit Trail & PII Access",
    summary: "Understanding the event logging system.",
    content: [
      "Every Master Admin access to identified candidate data is logged as an admin_pii_view event. This includes viewing the Candidate Database, opening a candidate detail, and any API call that returns PII fields.",
      "The Activity Feed in Platform Analytics shows all events in real time. Filter by event type or organization to investigate specific activity.",
      "Export event logs to CSV for compliance reporting. The export includes timestamp, event type, user name, organization, and demo status.",
      "Rate limits protect against excessive API usage: demo orgs are limited to 20 Deep Analyses per day, paid orgs to 500.",
    ],
  },
];

function ArticleCard({ article, expanded, onToggle }) {
  return (
    <Card className={`rounded-xl border transition-all ${expanded ? "shadow-md" : "hover:shadow-sm hover:border-slate-300"}`}>
      <CardContent className="p-0">
        <button onClick={onToggle} className="w-full flex items-start gap-4 p-5 text-left">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <article.icon className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px] font-accent mb-1">{article.category}</Badge>
            <h3 className="text-sm font-heading text-slate-900">{article.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{article.summary}</p>
          </div>
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />}
        </button>
        {expanded && (
          <div className="px-5 pb-5 border-t border-slate-100 pt-4 animate-slideDown">
            <div className="space-y-3">
              {article.content.map((p, i) => (
                <p key={i} className="text-sm text-slate-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function HelpCenter() {
  const { isAdmin } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const articles = showAdmin && isAdmin ? ADMIN_ARTICLES : CLIENT_ARTICLES;
  const filtered = searchText
    ? articles.filter(a => a.title.toLowerCase().includes(searchText.toLowerCase()) || a.summary.toLowerCase().includes(searchText.toLowerCase()))
    : articles;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="help-center">
      <div className="text-center mb-8 animate-fadeIn">
        <h1 className="text-2xl font-heading text-slate-900 mb-2">Help & Knowledge Base</h1>
        <p className="text-sm text-slate-500 max-w-lg mx-auto">
          Learn how Varyynt Intel's proprietary intent data helps you find candidates who aren't just qualified — they're interested.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search help articles..." className="pl-10" data-testid="help-search" />
        </div>
        {isAdmin && (
          <div className="flex bg-white rounded-lg border p-1">
            <button onClick={() => setShowAdmin(false)} className={`px-3 py-1.5 text-xs font-accent rounded-md transition-colors ${!showAdmin ? "bg-lime-50 text-lime-700" : "text-slate-500"}`}>
              User Guide
            </button>
            <button onClick={() => setShowAdmin(true)} className={`px-3 py-1.5 text-xs font-accent rounded-md transition-colors ${showAdmin ? "bg-lime-50 text-lime-700" : "text-slate-500"}`}>
              Admin Guide
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {filtered.map(a => (
          <ArticleCard key={a.id} article={a} expanded={expandedId === a.id} onToggle={() => setExpandedId(expandedId === a.id ? null : a.id)} />
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">No articles match your search.</div>
        )}
      </div>

      <div className="mt-8 bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white rounded-xl p-6 text-center animate-slideUp">
        <h3 className="font-heading text-lg mb-2">Still have questions?</h3>
        <p className="text-sm text-slate-300 mb-1">Contact the Varyynt team for personalized support.</p>
        <p className="text-xs text-slate-400 font-mono">support@varyynt.com</p>
      </div>
    </div>
  );
}
